'use client';

import { cva, cx, type VariantProps } from 'class-variance-authority';
import type { ClassValue } from 'class-variance-authority/types';
import Image from 'next/image';
import { useEffect, useState } from 'react';

const Avatar: React.FC<{
    className?: string;
    initialsClassName?: ClassValue;
    label?: string;
    imageUrl?: string;
    /** @default 'gray' */
    color?: NonNullable<VariantProps<typeof VARIANTS>['color']>;
    /** Tailwind-style number for the size. (e.g. 12 = 3rem)
     * @default 12 */
    size?: NonNullable<VariantProps<typeof VARIANTS>['size']>;
    /** Whether or not to hide the initials when the profile picture has loaded
     * @default false */
    hideInitials?: boolean;
}> = props => {
    const [showImage, setShowImage] = useState(!!props.imageUrl);
    useEffect(() => {
        setShowImage(!!props.imageUrl);
    }, [props.imageUrl]);

    return (
        <div
            className={VARIANTS({
                className: [
                    'relative inline-flex items-center justify-center overflow-hidden border bg-cover',
                    props.className,
                ],
                color: props.color,
                size: props.size,
            })}
            style={{ backgroundImage: props.imageUrl && `url(${props.imageUrl})` }}
        >
            {showImage && (
                <Image
                    className="absolute inset-0 h-full w-full object-cover"
                    src={props.imageUrl!}
                    alt="Profile picture"
                    fill
                    sizes="(max-width: 768px) 160px, 160px"
                    onError={() => {
                        setShowImage(false);
                    }}
                    // width={props.size === '6' ? 24 : 48}
                    // height={props.size === '6' ? 24 : 48}
                />
            )}
            {props.label && (
                <p className={cx(!props.hideInitials && 'relative z-1', props.initialsClassName)}>
                    {props.label.substring(0, 2).toUpperCase()}
                </p>
            )}
        </div>
    );
};

export default Avatar;

const VARIANTS = cva('', {
    variants: {
        color: {
            custom: 'background-custom-light',
            secondary: 'border-secondary bg-secondary text-white',
            yellow: 'border-[#F9B139] bg-[#F9B139] text-white',
            gray: 'border-gray-200 bg-gray-100 text-gray-700',
            blue: 'border-blue-200 bg-blue-50 text-blue-700',
            green: 'border-green-200 bg-green-50 text-green-700',
            red: 'border-red-200 bg-red-50 text-red-700',
        },
        size: {
            '12': 'h-12 w-12 rounded-xl p-2 font-medium',
            '6': 'h-6 w-6 rounded-lg p-1 text-2xs font-bold leading-none',
        },
    },
    defaultVariants: {
        color: 'gray',
        size: '12',
    },
});
